import * as Tone from 'tone';
import EventEmitter from 'eventemitter3';
import Grid from './Grid';
import { IGrid, IGridRenderer } from './Interfaces';
import { AnyEventTarget, Listener } from './InternalTypes';
import {
  GridRendererEventMap,
  ParseEventMap,
  ToneMatrixEventMap,
  ToneTransportEventMap,
} from './Types';
import GridCanvasRenderer from './GridCanvasRenderer';
import Util, { DEFAULT } from './Util';

export interface ToneMatrixOptions<
  GridType extends IGrid | undefined,
  GridRendererType extends IGridRenderer | undefined,
> {
  /**
   * If this property is provided it will be used as the gridPlayer
   * instead of making a default {@link Grid}
   * @default new {@link Grid}({@link ToneMatrixOptions.width width} = 16,
   * {@link ToneMatrixOptions.width height} = 16,
   * {@link ToneMatrixOptions.noteLength noteLength})
   */
  grid?: GridType;
  /**
   * If this property is provided it will be used as the gridRenderer
   * instead of making a default {@link GridCanvasRenderer}
   * @default new {@link GridCanvasRenderer}
   * ({@link ToneMatrixOptions.canvasWrapperEl canvasWrapperEl},
   * {@link IGrid.width},
   * {@link IGrid.height})
   */
  gridRenderer?: GridRendererType;
  /**
   * The DOM element to append the default gridRenderer.
   * Required if {@link ToneMatrixOptions.gridRenderer gridRenderer} is not provided,
   * ignored otherwise
   */
  canvasWrapperEl?: HTMLElement;
  /**
   * The width of the board to pass to the default Grid constructor.
   * Only used if {@link ToneMatrixOptions.grid grid} is provided.
   * @default 16
   */
  width?: number;
  /**
   * The height of the board to pass to the default Grid constructor.
   * Only used if {@link ToneMatrixOptions.grid grid} is provided.
   * @default 16
   */
  height?: number;
  /**
   * Note length, measured in fraction of a Tone measure.
   * @default 1 / {@link IGrid.width}
   */
  noteLength?: number;
  /**
   * Whether to autoplay the Grid, either at first page interaction or
   * asap if possible.
   */
  autoPlay?: boolean;
}

export type ListenerEventMaps = {
  [key in
  | keyof ParseEventMap<HTMLElementEventMap>
  | keyof GridRendererEventMap
  | keyof ToneTransportEventMap
  ]:
  | (key extends keyof ParseEventMap<HTMLElementEventMap>
    ? ParseEventMap<HTMLElementEventMap>[key]
    : never)
  | (key extends keyof GridRendererEventMap
    ? GridRendererEventMap[key]
    : never)
  | (key extends keyof ToneTransportEventMap
    ? ToneTransportEventMap[key]
    : never)
};

function getRenderer<G extends IGrid | undefined, GR extends IGridRenderer | undefined>(
  options: ToneMatrixOptions<G, GR>,
  width: number,
  height: number,
): (GR extends undefined ? GridCanvasRenderer : GR) {
  if (options.gridRenderer == null) {
    if (options.canvasWrapperEl == null) {
      throw new TypeError('Either gridRenderer or canvasWrapperEl must be defined.');
    }
    return new GridCanvasRenderer(
      options.canvasWrapperEl, width, height,
    ) as (GR extends undefined ? GridCanvasRenderer : GR);
  }
  return options.gridRenderer as (GR extends undefined ? GridCanvasRenderer : GR);
}

/**
 * Main class of ToneMatrix Redux, a pentatonic step sequencer
 */
export default class ToneMatrix<
  GridType extends IGrid | undefined = undefined,
  GridRendererType extends IGridRenderer | undefined = undefined,
> extends EventEmitter<ToneMatrixEventMap> {
  /**
   * The width of the grid, measured in grid tiles
   * @readonly
   */
  get WIDTH(): number {
    return this.grid.width;
  }

  /**
   * The height of the grid, measured in grid tiles
   * @readonly
   */
  get HEIGHT(): number {
    return this.grid.height;
  }

  /**
   * The grid connected to the ToneMatrix
   * @readonly
   */
  public readonly grid: GridType extends undefined ? Grid : GridType;
  /**
   * The gridrenderer connected to the ToneMatrix
   * @readonly
   */
  public readonly renderer: GridRendererType extends undefined
    ? GridCanvasRenderer
    : GridRendererType;

  /**
   * @internal
   */
  private internalListeners: Listener<ListenerEventMaps, keyof ListenerEventMaps>[] = [];

  private destroyed = false;
  private autoPlay: boolean;
  public readonly playing = false;

  /**
   * Creates a new ToneMatrix Redux instance, and attach it to existing DOM elements
   * @param options {object}
   * @param options.width test
   */
  constructor(
    options: ToneMatrixOptions<GridType, GridRendererType>,
  ) {
    super();
    const {
      height = 16,
      width = 16,
      grid = undefined,
      noteLength = undefined,
      autoPlay = false,
    } = options;

    this.grid = (typeof grid === 'undefined' ? new Grid(width, height, noteLength) : grid) as (GridType extends undefined ? Grid : GridType);
    this.renderer = getRenderer<GridType, GridRendererType>(
      options,
      this.grid.width, this.grid.height,
    );

    // Listen for clicks on the canvas

    // Whether our cursor is currently turning on or turning off tiles
    let arming: boolean | null = null;

    const tileClick = (x: number, y: number) => {
      if (arming === null) arming = !this.grid.getTileValue(x, y);
      this.grid.setTileValue(x, y, arming);
    };
    this.listen(this.renderer, 'tilemove', (x, y) => {
      this.emit('tilemove', x, y);
      tileClick(x, y);
    });
    this.listen(this.renderer, 'tiledown', (x, y) => {
      this.emit('tiledown', x, y);
      arming = null;
      tileClick(x, y);
    });
    this.listen(this.renderer, 'tileup', (...args) => {
      this.emit('tileup', ...args);
    });
    this.listen(this.renderer, 'tileclick', (...args) => {
      this.emit('tileclick', ...args);
    });

    this.autoPlay = autoPlay;
    if (autoPlay) {
      const events: [target: ToneMatrix['internalListeners'][number]['target'], event: ToneMatrix['internalListeners'][number]['event']][] = [
        [window, 'click'],
        [window, 'touchstart'],
      ];
      const startPlayingListener = () => {
        if (!this.autoPlay) {
          events.forEach(([target, event]) => {
            this.unlisten(target, event, startPlayingListener);
          });
        }
        Tone.context.resume().then(() => {
          Tone.Transport.start();
          if (autoPlay) {
            events.forEach(([target, event]) => {
              this.unlisten(target, event, startPlayingListener);
            });
            Object.assign(this, { playing: true, autoPlay: false });
          }
        });
      };
      events.forEach(([target, event]) => {
        this.listen(target, event, startPlayingListener);
      });
      startPlayingListener();
    }
    switch (Tone.Transport.state) {
      case 'paused':
        this.emit('pause', Tone.context.currentTime);
        break;
      case 'started':
        this.emit('start', Tone.context.currentTime, Tone.Time(Tone.Transport.loopStart).toSeconds());
        break;
      default:
    }
    const transport = Tone.Transport as unknown as EventEmitter<ToneTransportEventMap>;
    this.listen(transport, 'start', (...args) => {
      this.emit('start', ...args);
    });
    this.listen(transport, 'pause', (time: number) => {
      this.emit('pause', time);
    });
    this.listen(transport, 'stop', (...args) => {
      this.emit('stop', ...args);
    });
    this.listen(transport, 'loopEnd', (...args) => {
      this.emit('loopEnd', ...args);
    });
    this.listen(transport, 'loopStart', (...args) => {
      this.emit('loopStart', ...args);
    });
    this.listen(transport, 'loop', (...args) => {
      this.emit('loop', ...args);
    });

    // Kick off game loop

    const updateContinuous = () => {
      if (this.destroyed) return;
      this.update();
      requestAnimationFrame(updateContinuous);
    };
    requestAnimationFrame(updateContinuous);
  }

  /**
   * Updates the state of the app, and draws it to the canvas.
   * Called in requestAnimationFrame.
   */
  private update(): void {
    if ('update' in this.grid && this.grid.update != null) this.grid.update();
    this.renderer.update(this.grid);
  }

  /**
   * Start the playback
   */
  play(): void {
    this.autoPlay = false;
    if (!this.playing) {
      Tone.start().then(() => {
        Tone.Transport.start();
      });
    }
  }

  /**
   * Pause the playback
   */
  pause(): void {
    this.autoPlay = false;
    Tone.Transport.pause();
  }

  /**
  * Pause the playback
  */
  stop(): void {
    this.autoPlay = false;
    Tone.Transport.stop();
  }

  /**
   * Clears all notes from the grid and resets the sharing URL.
   */
  clear(): void {
    this.grid.clearAllTiles();
  }

  /**
   * Sets whether the ToneMatrix application is muted.
   * @param muted - True for muted, false for unmuted
   */
  setMuted(muted: boolean): void {
    this.grid.setMuted(muted);
  }

  /**
   * Saves the grid's current state into a savestate string
   * @returns The base64-encoded savestate string,
   *   ready for saving or outputting in a URL
   */
  toBase64(bitsPerNote: number): string {
    const bytes = new Uint8Array(Math.ceil((this.grid.data.length * bitsPerNote) / 8));
    let byteIndex = 0;
    let byte = 0;
    let bitOffset = 0;
    this.grid.data.forEach((tile) => {
      const bits = tile.getBitmask();
      for (let b = 0; b < bitsPerNote; b++) {
        if (bitOffset >= 8) {
          bytes[byteIndex] = byte;
          byteIndex += 1;
          byte = 0;
          bitOffset = 0;
        }
        // eslint-disable-next-line no-bitwise
        byte |= ((bits & (1 << b)) >> b) << bitOffset;
        bitOffset += 1;
      }
    });
    if (bitOffset !== 0) {
      bytes[byteIndex] = byte;
    }

    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Loads a savestate from a string into the grid
   * @param base64 - The base64-encoded savestate string
   */
  fromBase64(base64: string, bitsPerNote: number): boolean {
    try {
      this.grid.clearAllTiles();
      const binary = atob(base64);

      let noteIndex = 0;
      let noteBuffer = 0;
      let bitIndex = 0;
      for (let i = 0; i < binary.length; i += 1) {
        const byte = binary.charCodeAt(i);
        for (let bit = 0; bit < 8; bit++) {
          if (bitIndex >= bitsPerNote) {
            const [x, y] = Util.indexToCoord(noteIndex, this.grid.height);
            for (let b = 0; b < bitsPerNote; b++) {
              // eslint-disable-next-line no-bitwise
              if ((noteBuffer & (1 << b)) > 0) {
                this.grid.setTileValue(x, y, true, b);
              }
            }
            if (noteIndex >= this.grid.data.length) break;
            noteIndex += 1;
            noteBuffer = 0;
            bitIndex = 0;
          }
          // eslint-disable-next-line no-bitwise
          noteBuffer |= ((byte & (1 << bit)) >> bit) << bitIndex;
          bitIndex += 1;
        }
      }
      if (noteIndex < this.grid.data.length && bitIndex !== 0) {
        const [x, y] = Util.indexToCoord(noteIndex, this.grid.height);
        for (let b = 0; b < bitsPerNote; b++) {
          // eslint-disable-next-line no-bitwise
          if ((noteBuffer & (1 << b)) > 0) {
            this.grid.setTileValue(x, y, true, b);
          }
        }
        noteIndex += 1;
        noteBuffer = 0;
        bitIndex = 0;
      }
      return true;
    } catch (e) {
      console.error(e);
    }
    return false;
  }

  /**
   * Cleans up all resources used by this ToneMatrix
   */
  dispose(): void {
    this.destroyed = true;
    if ('dispose' in this.grid && this.grid.dispose != null) this.grid.dispose();
    if ('dispose' in this.renderer && this.renderer.dispose != null) this.renderer.dispose();
    this.internalListeners.forEach((listener) => {
      if (listener.target.off != null) {
        listener.target.off(
          listener.event,
          listener.cb as () => void,
        );
      } else if (listener.target.removeEventListener != null) {
        listener.target.removeEventListener(
          listener.event,
          listener.cb as () => void,
        );
      } else if (listener.target.removeListener != null) {
        listener.target.removeListener(
          listener.event,
          listener.cb as () => void,
        );
      }
    });
    // restore mute status
    Tone.Destination.mute = false;
    this.internalListeners = [];
  }

  /**
   * Set a new grid size
   * @param newWidth New width in tiles
   * @param newHeight New height in tiles
   * @param noteLength New noteLength.
   * if {@link DEFAULT | `DEFAULT`} it will be set to {@link IGrid.width | `1 / width`}
   * if unset will recalculate the default value if using a calcualted default value
   * or keep the currently set value if explicitly set.
   */
  setSize(
    newWidth?: number,
    newHeight?: number,
    noteLength?: number | typeof DEFAULT,
  ) {
    this.grid.setSize(newWidth, newHeight, noteLength);
    this.renderer.setSize(this.grid.width, this.grid.height);
  }

  setWidth(newWidth: number, noteLength?: number | typeof DEFAULT) {
    this.setSize(newWidth, undefined, noteLength);
  }

  setHeight(newHeight: number, noteLength?: number | typeof DEFAULT) {
    this.setSize(undefined, newHeight, noteLength);
  }

  /**
   * Add an event listener to a listenable object and save everything needed
   * for removing it later.
   * @internal
   */
  private listen<
  P extends keyof ListenerEventMaps,
  Args extends ListenerEventMaps[P] = ListenerEventMaps[P],
  T extends AnyEventTarget<ListenerEventMaps, P, Args> = AnyEventTarget<ListenerEventMaps, P, Args>,
  >(
    target: T,
    event: P,
    listener: (...args: Args) => void,
  ): this {
    this.internalListeners.push({
      target: target as unknown as AnyEventTarget<ListenerEventMaps, keyof ListenerEventMaps>,
      event,
      cb: listener as Listener<ListenerEventMaps, keyof ListenerEventMaps>['cb'],
    });
    if (target.on != null) {
      target.on(event, listener);
    } else if (target.addEventListener != null) {
      target.addEventListener(event, listener);
    } else if (target.addListener != null) {
      target.addListener(event, listener);
    }
    return this;
  }

  /**
  * Remove a listener from a listenable object.
  * @internal
  */
  private unlisten<
  P extends keyof ListenerEventMaps,
  Args extends ListenerEventMaps[P] = ListenerEventMaps[P],
  T extends AnyEventTarget<ListenerEventMaps, P, Args> = AnyEventTarget<ListenerEventMaps, P, Args>,
  >(
    target: T,
    event: P,
    listener: (...args: Args) => void,
  ): this {
    const toRemove: typeof this.internalListeners = [];
    this.internalListeners = this.internalListeners.filter((entry) => {
      if (entry.target === target && entry.event === event && entry.cb === listener) {
        toRemove.push(entry);
        return false;
      }
      return true;
    });
    toRemove.forEach((entry) => {
      if (entry.target.off != null) {
        entry.target.off(
          entry.event,
          entry.cb as () => void,
        );
      } else if (entry.target.removeEventListener != null) {
        entry.target.removeEventListener(
          entry.event,
          entry.cb as () => void,
        );
      } else if (entry.target.removeListener != null) {
        entry.target.removeListener(
          entry.event,
          entry.cb as () => void,
        );
      }
    });
    return this;
  }
}
